import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import { Search, Download, RefreshCw, CheckCircle, XCircle, Clock, Printer } from "lucide-react";
import useApi from "../../../CustomHooks/useApi";
import { openPrint } from "../../../Store/Slices/printSlice";
import PrintPopup from "../../../ResuableCompoment/PrintPopup";

const statusBadge = (s) => {
  if (s === "SUCCESS")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        <CheckCircle className="w-3 h-3" /> SUCCESS
      </span>
    );
  if (s === "FAILED" || s === "FAILURE")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        <XCircle className="w-3 h-3" /> FAILED
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
      <Clock className="w-3 h-3" /> {s || "PENDING"}
    </span>
  );
};

export default function PPIHistory() {
  const mid = useSelector((s) => s.auth.employeeDetails?.username);
  const dispatch = useDispatch();
  const { callApi: fetchHistory } = useApi("/ppi/transaction/history", "GET");

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 25;

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await fetchHistory(null, `username=${mid}`);
      const list = Array.isArray(res) ? res : [];
      setRecords(list.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = records.filter((r) =>
    Object.values(r).some((v) =>
      String(v).toLowerCase().includes(search.toLowerCase())
    )
  );

  const totalPages = Math.ceil(filtered.length / perPage) || 1;
  const paged = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const downloadXlsx = () => {
    const ws = XLSX.utils.json_to_sheet(
      records.map((r) => ({
        Date: r.date,
        "Txn ID": r.txnId,
        Status: r.status,
        "Net Amount": r.netAmount ?? r.amount,
        "Remitter Mobile": r.remitterMobile,
        "Beneficiary Name": r.beneName,
        "Account No": r.beneAccountno,
        "IFSC Code": r.beneIfscCode,
        Bank: r.beneBankName,
        "Bank RRN": r.bankRrn,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PPI History");
    XLSX.writeFile(wb, "PPI_Transaction_History.xlsx");
  };


  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          PPI Transaction History
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          All PPI wallet transfers from your terminal
        </p>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-56">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search transactions…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={loadHistory}
          disabled={loading}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />{" "}
          Refresh
        </button>
        <button
          onClick={downloadXlsx}
          disabled={records.length === 0}
          className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-60"
        >
          <Download className="w-4 h-4" /> Excel
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[
                  "Date",
                  "Txn ID",
                  "Status",
                  "Net Amount",
                  "Remitter",
                  "Beneficiary",
                  "Account",
                  "Bank RRN",
                  "Receipt",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td
                    colSpan={9}
                    className="py-12 text-center text-gray-400 text-sm"
                  >
                    Loading…
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="py-12 text-center text-gray-400 text-sm"
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                paged.map((r, i) => (
                  <tr key={r.txnId || i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {r.date ? new Date(r.date).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">
                      {r.txnId || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {statusBadge(r.status)}
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap font-variant-numeric tabular-nums">
                      ₹{Number(r.netAmount ?? r.amount ?? 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.remitterMobile || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{r.beneName || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">
                      {r.beneAccountno || "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {r.bankRrn || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => dispatch(openPrint({
                          ...r,
                          beneficiaryName:    r.beneName,
                          beneficiaryAccountNo: r.beneAccountno,
                          beneficiary_ifsc_code: r.beneIfscCode,
                          remitterMobileNumber: r.remitterMobile,
                        }))}
                        className="p-1.5 rounded-xl border border-slate-200 text-slate-600 hover:text-blue-600 hover:border-blue-200 bg-white hover:bg-slate-50 transition active:scale-95 inline-flex items-center gap-1 shadow-sm cursor-pointer"
                        title="Print Receipt"
                      >
                        <Printer className="w-3.5 h-3.5" />
                        <span className="text-[11px] font-semibold">Print</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filtered.length > perPage && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
            <span>
              Page {currentPage} of {totalPages} &nbsp;·&nbsp;{" "}
              {filtered.length} records
            </span>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Prev
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <PrintPopup />
    </div>
  );
}
